"use client";

import { Leaf } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ThePathToFunding() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: stepsRef,
    isVisible: stepsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const steps = [{
    number: "01",
    title: "CREDIT ARCHITECTURE",
    description: "We analyze your current profile to engineer a precise blueprint for elite credit positioning.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/financial_blueprint_analysis.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/financial_blueprint_analysis.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/financial_blueprint_analysis.png-wvc-sizes",
    alt: "Financial blueprint and architectural tools",
    rotate: "-rotate-2"
  }, {
    number: "02",
    title: "BUSINESS DEVELOPMENT",
    description: "Establishing corporate credibility and building trade lines that separate personal and business assets.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/corporate_structure_growth.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/corporate_structure_growth.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/corporate_structure_growth.png-wvc-sizes",
    alt: "Modern corporate office skyline",
    rotate: "rotate-1"
  }, {
    number: "03",
    title: "FUNDING ACQUISITION",
    description: "Accessing high-limit capital through our network of selective lending partners and private sources.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/capital_funding_vault.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/capital_funding_vault.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/capital_funding_vault.png-wvc-sizes",
    alt: "Luxury vault and capital assets",
    rotate: "-rotate-1"
  }, {
    number: "04",
    title: "WEALTH STRATEGY",
    description: "Deploying capital into strategic pathways designed for long-term preservation and generational growth.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/wealth_strategy_consultation.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/wealth_strategy_consultation.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695823/img/wealth_strategy_consultation.webp-wvc-sizes",
    alt: "Private wealth management consultation",
    rotate: "rotate-2"
  }];
  return <section data-section-id="4933" id="the-path-to-funding" className="relative overflow-hidden bg-background text-foreground py-32 md:py-48 lg:py-[200px] px-6 md:px-12">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
        backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} />
        <Leaf className="absolute top-20 left-10 w-64 h-64 text-primary" strokeWidth={0.5} />
        <Leaf className="absolute top-1/3 right-20 w-80 h-80 text-primary rotate-45" strokeWidth={0.5} />
        <Leaf className="absolute bottom-40 left-1/3 w-72 h-72 text-primary -rotate-12" strokeWidth={0.5} />
        <Leaf className="absolute bottom-20 right-10 w-56 h-56 text-primary rotate-90" strokeWidth={0.5} />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`mb-24 md:mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:pl-[10%]">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-12 bg-primary" />
              <p className="font-mono text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-primary">
                § 01 — The Blueprint
              </p>
            </div>
            <h2 className="font-default font-medium uppercase leading-[1.1] tracking-tighter text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[5rem]">
              The Path To
              <br />
              <span className="block text-primary">Funding.</span>
            </h2>
            <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground mt-10 max-w-[600px]">
              A structured methodology for high-net-worth individuals and entrepreneurs. We engineer the financial infrastructure required for elite capital access.
            </p>
          </div>
        </div>

        {}
        <div ref={stepsRef} className="relative">
          {}
          <div className="hidden lg:block absolute top-[3rem] left-[8%] right-[8%] h-px bg-border z-0" />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 lg:gap-8 relative z-10">
            {steps.map((step, i) => <div data-index={i} key={step.number} className={`relative flex flex-col items-center lg:items-start transition-all duration-1000 ${stepsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                {}
                <div className="hidden lg:block absolute -top-1 left-1/2 -translate-x-1/2 z-20">
                  <div className="w-3 h-3 bg-primary" />
                </div>

                {}
                <div className="relative mb-8 w-full flex justify-center lg:justify-start">
                  <Leaf className="absolute -top-4 left-2 w-24 h-24 text-primary opacity-10 -rotate-12" strokeWidth={0.8} />
                  <span className="relative font-default font-bold text-[6rem] md:text-[7rem] leading-none tracking-tighter select-none" style={{
                WebkitTextStroke: "1.5px var(--foreground)",
                color: "transparent",
                backgroundImage: "linear-gradient(to bottom, transparent 0%, transparent 55%, var(--primary) 55%, var(--primary) 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text"
              }}>
                    {step.number}
                  </span>
                </div>

                {}
                <div className={`mb-6 transform ${step.rotate}`}>
                  <div className="relative">
                    <div className="absolute inset-0 translate-x-3 translate-y-3 bg-primary opacity-20" />
                    <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="relative w-full max-w-[260px] aspect-square object-cover grayscale border border-border rounded-md shadow-lg" />
                  </div>
                </div>

                {}
                <h3 className="font-default font-semibold uppercase tracking-[0.05em] text-foreground text-sm mb-3 text-center lg:text-left w-full">
                  {step.title}
                </h3>

                {}
                <p className="font-default text-sm leading-[1.6] text-muted-foreground text-center lg:text-left max-w-[260px]">
                  {step.description}
                </p>
              </div>)}
          </div>
        </div>

        {}
        <div className="mt-32 md:mt-40 flex justify-end">
          <div className="flex items-center gap-4">
            <span className="h-px w-16 bg-primary" />
            <p className="font-mono text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-foreground">
              Architected for Excellence / Benewealth Financial Est. 2024
            </p>
          </div>
        </div>
      </div>
    </section>;
}